import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar, Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getHolidays } from "../../services/hrms.service";

const ManagerHolidays = () => {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMonth, setFilterMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const currentYear = new Date().getFullYear();

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  useEffect(() => {
    loadHolidays();
  }, [selectedYear, page]);

  useEffect(() => {
    filterHolidayList();
  }, [searchTerm, filterMonth, holidays]);

  const loadHolidays = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getHolidays(selectedYear, page, 100); // Get more holidays for filtering
      
      if (response.status === 1) {
        // Handle both array and object with data property
        const holidaysData = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.data || []);
        
        // Transform API data to match component structure
        const transformedHolidays = holidaysData.map(holiday => {
          const holidayDate = new Date(holiday.holiday_date);
          const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
          
          return {
            id: holiday.id,
            name: holiday.holiday_name,
            date: holiday.holiday_date,
            day: days[holidayDate.getDay()],
            type: holiday.holiday_type || "National Holiday",
            description: holiday.description || ""
          };
        });

        setHolidays(transformedHolidays);
        setFilteredHolidays(transformedHolidays);
        
        // Calculate total pages
        const total = response.total || response.data?.total || transformedHolidays.length;
        setTotalPages(Math.ceil(total / 100));
      } else {
        setHolidays([]);
        setFilteredHolidays([]);
        setError(response.message || "Failed to load holidays");
      }
    } catch (err) {
      console.error("Error loading holidays:", err);
      setError("Failed to load holidays. Please try again.");
      setHolidays([]);
      setFilteredHolidays([]);
    } finally {
      setLoading(false);
    }
  };

  const filterHolidayList = () => {
    let filtered = [...holidays];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(holiday =>
        holiday.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        holiday.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Month filter
    if (filterMonth !== "all") {
      filtered = filtered.filter(holiday => {
        const holidayMonth = new Date(holiday.date).getMonth();
        return holidayMonth === parseInt(filterMonth);
      });
    }

    setFilteredHolidays(filtered);
  };

  const getHolidayTypeColor = (type) => {
    const colors = {
      "National Holiday": "bg-red-100 text-red-800 border-red-300",
      "Festival": "bg-purple-100 text-purple-800 border-purple-300",
      "Religious Holiday": "bg-blue-100 text-blue-800 border-blue-300",
      "Company Holiday": "bg-green-100 text-green-800 border-green-300",
    };
    return colors[type] || "bg-gray-100 text-gray-800 border-gray-300";
  };

  const isUpcoming = (dateStr) => {
    if (!dateStr) return false;
    const holidayDate = new Date(dateStr);
    holidayDate.setHours(0, 0, 0, 0); // Set to midnight for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to midnight for accurate comparison
    return holidayDate >= today;
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return 0;
    const holidayDate = new Date(dateStr);
    holidayDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = holidayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading holidays...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Holidays</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadHolidays}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const upcomingHolidays = filteredHolidays.filter(h => isUpcoming(h.date));
  const pastHolidays = filteredHolidays.filter(h => !isUpcoming(h.date));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/hrms/manager-dashboard")}
          className="flex items-center text-indigo-600 hover:text-indigo-700 mb-6 font-medium"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Company Holidays {selectedYear}
              </h1>
              <p className="text-gray-600">
                View all holidays for {selectedYear}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {[currentYear - 1, currentYear, currentYear + 1].map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-indigo-600" />
              <span className="text-3xl font-bold text-indigo-700">{holidays.length}</span>
            </div>
            <p className="text-sm font-semibold text-gray-700">Total Holidays</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-green-600" />
              <span className="text-3xl font-bold text-green-700">{upcomingHolidays.length}</span>
            </div>
            <p className="text-sm font-semibold text-gray-700">Upcoming</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-red-600" />
              <span className="text-3xl font-bold text-red-700">
                {holidays.filter(h => h.type === "National Holiday").length}
              </span>
            </div>
            <p className="text-sm font-semibold text-gray-700">National Holidays</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="w-8 h-8 text-purple-600" />
              <span className="text-3xl font-bold text-purple-700">
                {holidays.filter(h => h.type === "Festival").length}
              </span>
            </div>
            <p className="text-sm font-semibold text-gray-700">Festivals</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search holidays..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Month Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none"
              >
                <option value="all">All Months</option>
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Upcoming Holidays Section */}
        {upcomingHolidays.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Upcoming Holidays</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingHolidays.map((holiday) => (
                <div
                  key={holiday.id}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800">{holiday.name}</h3>
                        <p className="text-sm text-gray-500">{holiday.day}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-2xl font-bold text-indigo-600 mb-1">
                      {new Date(holiday.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${getHolidayTypeColor(holiday.type)}`}>
                      {holiday.type}
                    </span>
                  </div>

                  <p className="text-sm text-gray-600 mb-3">{holiday.description}</p>

                  {getDaysUntil(holiday.date) >= 0 && (
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-sm font-semibold text-green-700">
                        {getDaysUntil(holiday.date) === 0
                          ? "Today!"
                          : getDaysUntil(holiday.date) === 1
                          ? "Tomorrow"
                          : `${getDaysUntil(holiday.date)} days to go`}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Past Holidays Section */}
        {pastHolidays.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Past Holidays</h2>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="divide-y divide-gray-200">
                {pastHolidays.map((holiday) => (
                  <div key={holiday.id} className="p-6 hover:bg-gray-50 transition">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-lg flex flex-col items-center justify-center">
                          <span className="text-xs text-gray-500">
                            {new Date(holiday.date).toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                          <span className="text-2xl font-bold text-gray-700">
                            {new Date(holiday.date).getDate()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-800">{holiday.name}</h3>
                          <p className="text-sm text-gray-500">{holiday.day}</p>
                          <p className="text-sm text-gray-600 mt-1">{holiday.description}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getHolidayTypeColor(holiday.type)}`}>
                        {holiday.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredHolidays.length === 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No holidays found</p>
            <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerHolidays;